/**
 * Vanilla JS replacements for Bootstrap 5 JS components.
 * Handles: modal, tabs, collapse — zero dependencies.
 *
 * Attribute convention (replaces data-bs-*):
 *   data-toggle="modal|tab|collapse"
 *   data-target="#elementId"
 *   data-dismiss="modal"
 */

// ── Modal ───────────────────────────────────────────────────────

let activeModal: HTMLElement | null = null;
let backdrop: HTMLElement | null = null;

function showModal(modal: HTMLElement): void {
  if (activeModal) hideModal(activeModal);

  // Create backdrop
  backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop show";
  document.body.appendChild(backdrop);
  backdrop.addEventListener("click", () => hideModal(modal));

  modal.classList.add("show");
  modal.style.display = "block";
  document.body.style.overflow = "hidden";
  activeModal = modal;

  // Focus trap — focus first focusable element
  const focusable = modal.querySelector<HTMLElement>(
    "a[href], button:not([disabled]), input:not([disabled]), [tabindex]",
  );
  focusable?.focus();
}

function hideModal(modal: HTMLElement): void {
  modal.classList.remove("show");
  modal.style.display = "";
  document.body.style.overflow = "";

  if (backdrop) {
    backdrop.remove();
    backdrop = null;
  }
  activeModal = null;
}

// ── Tabs ────────────────────────────────────────────────────────

function switchTab(button: HTMLElement): void {
  const targetSelector = button.getAttribute("data-target");
  if (!targetSelector) return;

  const targetPane = document.querySelector<HTMLElement>(targetSelector);
  if (!targetPane) return;

  // Deactivate sibling tabs
  const tabList = button.closest('[role="tablist"], .nav');
  if (tabList) {
    tabList.querySelectorAll<HTMLElement>('[data-toggle="tab"], .nav-link').forEach((tab) => {
      tab.classList.remove("active");
      tab.setAttribute("aria-selected", "false");
    });
  }

  // Deactivate sibling panes
  const tabContent = targetPane.parentElement;
  if (tabContent) {
    tabContent.querySelectorAll(":scope > .tab-pane").forEach((pane) => {
      pane.classList.remove("active", "show");
    });
  }

  // Activate
  button.classList.add("active");
  button.setAttribute("aria-selected", "true");
  targetPane.classList.add("active", "show");

  // Dispatch custom event for tag-widget recalc
  document.dispatchEvent(new CustomEvent("tab-switched", { detail: { tab: button, pane: targetPane } }));
}

// ── Collapse ────────────────────────────────────────────────────

function toggleCollapse(target: HTMLElement, trigger: HTMLElement): void {
  const isShown = target.classList.contains("show");

  if (isShown) {
    // Collapse: animate height → 0
    target.style.height = target.scrollHeight + "px";
    target.offsetHeight; // force reflow
    target.classList.remove("show");
    target.classList.add("collapsing");
    target.style.height = "0";
    target.addEventListener(
      "transitionend",
      () => {
        target.classList.remove("collapsing");
        target.classList.add("collapse");
        target.style.height = "";
      },
      { once: true },
    );
  } else {
    // Expand: animate 0 → scrollHeight
    target.classList.remove("collapse");
    target.classList.add("collapsing");
    target.style.height = "0";
    target.offsetHeight; // force reflow
    target.style.height = target.scrollHeight + "px";
    target.addEventListener(
      "transitionend",
      () => {
        target.classList.remove("collapsing");
        target.classList.add("collapse", "show");
        target.style.height = "";
      },
      { once: true },
    );
  }

  // Toggle aria
  const expanded = !isShown;
  trigger.setAttribute("aria-expanded", String(expanded));
  trigger.classList.toggle("collapsed", !expanded);
}

// ── Event Delegation ────────────────────────────────────────────

export function initUIComponents(): void {
  document.addEventListener("click", (e) => {
    const trigger = (e.target as Element)?.closest<HTMLElement>("[data-toggle]");
    if (!trigger) return;

    const action = trigger.getAttribute("data-toggle");
    const targetSelector = trigger.getAttribute("data-target");

    if (action === "modal" && targetSelector) {
      e.preventDefault();
      const modal = document.querySelector<HTMLElement>(targetSelector);
      if (modal) showModal(modal);
    }

    if (action === "tab" && targetSelector) {
      e.preventDefault();
      switchTab(trigger);
    }

    if (action === "collapse" && targetSelector) {
      e.preventDefault();
      const target = document.querySelector<HTMLElement>(targetSelector);
      if (target) toggleCollapse(target, trigger);
    }
  });

  // Dismiss buttons inside modals
  document.addEventListener("click", (e) => {
    const dismiss = (e.target as Element)?.closest<HTMLElement>("[data-dismiss='modal']");
    if (dismiss) {
      const modal = dismiss.closest<HTMLElement>(".modal");
      if (modal) hideModal(modal);
    }
  });

  // Close modal on backdrop click (clicking modal overlay itself)
  document.addEventListener("click", (e) => {
    if (activeModal && e.target === activeModal) {
      hideModal(activeModal);
    }
  });

  // Escape key closes modal
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && activeModal) {
      hideModal(activeModal);
    }
  });
}

initUIComponents();
